import { useEffect, useMemo, useRef, type Dispatch, type SetStateAction } from "react";

import {
  getNestedValue,
  setNestedValue,
} from "containers/admin/clients/client/components/form-builder/form-builder";
import { isRecord } from "containers/admin/clients/client/components/form-builder/form-builder-utils";
import type { JsonFormOption } from "containers/admin/clients/client/components/form-builder/form-schema";
import {
  getStrategyData,
  type StrategyFieldOverrides,
  type StrategyTabFieldOverrides,
} from "containers/admin/clients/client/programs-page/program-strategy-form.utils";

import {
  useCampaignObjectiveTypeOptions,
  useClassTypeOptions,
  useTouchpointContentTypeOptions,
} from "hooks/api/dictionary/dictionary";

import type { OptionType } from "ui/select";

export const CAMPAIGN_OBJECTIVE_FIELD_KEYS = ["campaignObjective", "apolloCampaignObjective"] as const;

const SELECT_OBJECTIVE_FIRST_HELPER_TEXT = "Select a campaign objective first";

// Also the "campaign-details" tab id — the section has no children, so its data path and its
// tab id are the same string (see program-strategy-form.utils.ts's `createStrategyFormTabs`).
const CAMPAIGN_DETAILS_SECTION_ID = "campaign-details";
const AUDIENCE_CREATIVE_TAB_IDS = ["creatives-student", "creatives-parent"] as const;

// True once a schema version drops the static objective/class/content-type lists (v5+) in favor
// of dictionary-sourced options — v1-v4 keep their static lists and are unaffected below.
const MIN_DYNAMIC_OPTIONS_SCHEMA_VERSION = 5;

const toJsonFormOptions = (options: OptionType[]): JsonFormOption[] =>
  options.map(({ label, value }) => ({ label: String(label), value }));

// Keyed by upper-cased name -> the dictionary's own casing, so a value that only differs by case
// (e.g. a Monday-prefilled "senior" against the dictionary's "SENIOR") is recognized as valid and
// normalized to the dictionary's casing, rather than silently dropped.
const buildAllowedLookup = (names: Iterable<string>): Map<string, string> =>
  new Map(Array.from(names, (name) => [name.toUpperCase(), name]));

const pruneItemClasses = (
  classes: unknown,
  allowedClasses: Map<string, string>,
): { classes: unknown; changed: boolean } => {
  if (!Array.isArray(classes)) {
    return { classes, changed: false };
  }

  const resolved = classes
    .map((value) => allowedClasses.get(String(value).toUpperCase()))
    .filter((value): value is string => value !== undefined);

  const changed =
    resolved.length !== classes.length || resolved.some((value, index) => value !== classes[index]);

  return changed ? { classes: resolved, changed: true } : { classes, changed: false };
};

const pruneItemContentType = (
  contentType: unknown,
  allowedContentTypes: Map<string, string>,
): { contentType: unknown; changed: boolean } => {
  if (contentType === undefined) {
    return { contentType, changed: false };
  }

  const canonical = allowedContentTypes.get(String(contentType).toUpperCase());

  return canonical === contentType
    ? { contentType, changed: false }
    : { contentType: canonical, changed: true };
};

const pruneAudienceCreativeSelections = (
  data: Record<string, unknown>,
  audienceKey: "student" | "parent",
  allowedClasses: Map<string, string>,
  allowedContentTypes: Map<string, string>,
): { data: Record<string, unknown>; changed: boolean } => {
  const items = getStrategyData(getNestedValue(data, ["creatives", audienceKey])).items;

  if (!Array.isArray(items)) {
    return { data, changed: false };
  }

  let audienceChanged = false;

  const prunedItems = items.map((item) => {
    if (!isRecord(item)) {
      return item;
    }

    const { classes, changed: classesChanged } = pruneItemClasses(item.classes, allowedClasses);
    const { contentType, changed: contentTypeChanged } = pruneItemContentType(
      item.contentType,
      allowedContentTypes,
    );

    if (!classesChanged && !contentTypeChanged) {
      return item;
    }

    audienceChanged = true;

    return { ...item, classes, contentType };
  });

  return audienceChanged
    ? { data: setNestedValue(data, ["creatives", audienceKey, "items"], prunedItems), changed: true }
    : { data, changed: false };
};

const pruneInvalidCreativeSelections = (
  data: Record<string, unknown>,
  allowedClasses: Map<string, string>,
  allowedContentTypes: Map<string, string>,
): { data: Record<string, unknown>; changed: boolean } => {
  const afterStudent = pruneAudienceCreativeSelections(data, "student", allowedClasses, allowedContentTypes);
  const afterParent = pruneAudienceCreativeSelections(
    afterStudent.data,
    "parent",
    allowedClasses,
    allowedContentTypes,
  );

  return { data: afterParent.data, changed: afterStudent.changed || afterParent.changed };
};

type UseCampaignObjectiveCascadeOverridesParams = {
  schemaVersion: number | undefined;
  programType: string | undefined;
  draftData: Record<string, unknown>;
  setDraftData: Dispatch<SetStateAction<Record<string, unknown>>>;
  isEditing: boolean;
  baseOverrides?: StrategyFieldOverrides;
};

/**
 * Resolves dictionary-sourced options for `campaignObjective` (filtered by the template's
 * `programType`) and the `campaignObjective`-dependent `classes`/`contentType` fields (disabled
 * with helper text until an objective is picked), and prunes now-invalid `classes`/`contentType`
 * selections across both student and parent creatives when the objective changes, while editing.
 * A no-op, returning just `baseOverrides`, against a schema version that still ships static
 * options.
 *
 * Shared by the campaign-template authoring page and the real-campaign strategy page — both
 * render the same JSON-schema-driven "Campaign details"/"Creatives" tabs.
 */
export const useCampaignObjectiveCascadeOverrides = ({
  schemaVersion,
  programType,
  draftData,
  setDraftData,
  isEditing,
  baseOverrides,
}: UseCampaignObjectiveCascadeOverridesParams): StrategyFieldOverrides => {
  const isDynamicOptionsSchema = (schemaVersion ?? 0) >= MIN_DYNAMIC_OPTIONS_SCHEMA_VERSION;

  const selectedCampaignObjective = CAMPAIGN_OBJECTIVE_FIELD_KEYS.map(
    (fieldKey) => getStrategyData(getNestedValue(draftData, [CAMPAIGN_DETAILS_SECTION_ID]))[fieldKey],
  ).find((value): value is string => typeof value === "string" && value.length > 0);

  const { campaignObjectiveTypeOptions } = useCampaignObjectiveTypeOptions(
    programType,
    isDynamicOptionsSchema,
  );
  const { classTypeOptions, data: classTypesData } = useClassTypeOptions(
    selectedCampaignObjective,
    isDynamicOptionsSchema,
  );
  const { contentTypeOptions, data: contentTypesData } = useTouchpointContentTypeOptions(
    selectedCampaignObjective,
    isDynamicOptionsSchema,
  );

  const previousCampaignObjectiveRef = useRef<string | undefined>(selectedCampaignObjective);
  const dictionaryDataReady = Boolean(classTypesData && contentTypesData);

  const fieldOverrides = useMemo<StrategyFieldOverrides>(() => {
    if (!isDynamicOptionsSchema) {
      return baseOverrides ?? {};
    }

    const gatedOverride = !selectedCampaignObjective
      ? { "x-disabled": true, description: SELECT_OBJECTIVE_FIRST_HELPER_TEXT }
      : dictionaryDataReady
        ? { "x-disabled": false, description: undefined }
        : { "x-disabled": true, description: undefined };

    const withOptions = (options: OptionType[]) => ({
      ...gatedOverride,
      ...(selectedCampaignObjective && dictionaryDataReady
        ? { "x-options": toJsonFormOptions(options) }
        : {}),
    });

    const creativeItemOverrides: StrategyTabFieldOverrides = {
      classes: withOptions(classTypeOptions),
      contentType: withOptions(contentTypeOptions),
    };

    return {
      ...baseOverrides,
      [CAMPAIGN_DETAILS_SECTION_ID]: {
        ...baseOverrides?.[CAMPAIGN_DETAILS_SECTION_ID],
        campaignObjective: { "x-options": toJsonFormOptions(campaignObjectiveTypeOptions) },
      },
      ...Object.fromEntries(AUDIENCE_CREATIVE_TAB_IDS.map((tabId) => [tabId, creativeItemOverrides])),
    };
  }, [
    baseOverrides,
    isDynamicOptionsSchema,
    selectedCampaignObjective,
    campaignObjectiveTypeOptions,
    classTypeOptions,
    contentTypeOptions,
    dictionaryDataReady,
  ]);

  useEffect(() => {
    const previousCampaignObjective = previousCampaignObjectiveRef.current;
    const campaignObjectiveChanged = previousCampaignObjective !== selectedCampaignObjective;
    previousCampaignObjectiveRef.current = selectedCampaignObjective;

    if (!isEditing || !isDynamicOptionsSchema || !selectedCampaignObjective) {
      return;
    }

    if (campaignObjectiveChanged && previousCampaignObjective !== undefined) {
      setDraftData((prev) => {
        const cleared = pruneInvalidCreativeSelections(prev, new Map(), new Map());

        return cleared.changed ? cleared.data : prev;
      });
    }

    if (!classTypesData || !contentTypesData) {
      return;
    }

    const allowedClasses = buildAllowedLookup(classTypesData.map((item) => item.name));
    const allowedContentTypes = buildAllowedLookup(contentTypesData.map((item) => item.name));

    setDraftData((prev) => {
      const pruned = pruneInvalidCreativeSelections(prev, allowedClasses, allowedContentTypes);

      return pruned.changed ? pruned.data : prev;
    });
  }, [
    isEditing,
    isDynamicOptionsSchema,
    selectedCampaignObjective,
    classTypesData,
    contentTypesData,
    setDraftData,
  ]);

  return fieldOverrides;
};
